/**
 * Pick a destination session and bulk-move selected review cases.
 */

import { useEffect, useState } from "react";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "@/components/ui/dialog/Dialog";
import { Button } from "@/components/ui/button/Button";
import type { SelectOption } from "@/components/ui/select-dropdown";

interface MoveRunsDialogProps {
  open: boolean;
  selectedCount: number;
  sessions: SelectOption<string>[];
  moving: boolean;
  onClose: () => void;
  onConfirm: (sessionId: string) => void;
}

export function MoveRunsDialog({
  open,
  selectedCount,
  sessions,
  moving,
  onClose,
  onConfirm,
}: MoveRunsDialogProps) {
  const [targetSessionId, setTargetSessionId] = useState("");

  useEffect(() => {
    if (!open) {
      setTargetSessionId("");
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} size="sm">
      <DialogTitle>Choose destination session</DialogTitle>
      <DialogBody className="space-y-3">
        <p className="text-sm text-slate-400">
          Move {selectedCount} selected case{selectedCount === 1 ? "" : "s"} to another review session.
        </p>
        <ul className="max-h-56 space-y-1 overflow-y-auto rounded-lg bg-white/[0.03] p-1 ring-1 ring-white/10">
          {sessions.map((session) => {
            const active = targetSessionId === session.value;
            return (
              <li key={session.value}>
                <button
                  type="button"
                  onClick={() => setTargetSessionId(session.value)}
                  className={
                    active
                      ? "w-full rounded-md bg-indigo-500/15 px-3 py-2 text-left text-sm text-indigo-100 ring-1 ring-indigo-400/40"
                      : "w-full rounded-md px-3 py-2 text-left text-sm text-slate-200 hover:bg-white/[0.04]"
                  }
                >
                  {session.label}
                </button>
              </li>
            );
          })}
        </ul>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={moving}>
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(targetSessionId)}
          disabled={moving || !targetSessionId}
          data-testid="agent-review-move-confirm"
        >
          {moving ? "Moving…" : "Move"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
